import React, { useMemo, useState } from "react";
import axios from "axios";

import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  InputAdornment,
  IconButton,
  Divider,
  Stack,
  LinearProgress,
  Tooltip,
} from "@mui/material";

import {
  Person,
  Email,
  Phone,
  Lock,
  Home,
  Bloodtype,
  Wc,
  CalendarMonth,
  Visibility,
  VisibilityOff,
  Check,
  ArrowBack,
  WhatsApp,
  ArrowForward,
  Close,
} from "@mui/icons-material";

import { toast } from "react-toastify";

import { useNavigate } from "react-router-dom";
import logo from "../images/caredesk360logonew.jpeg";
import logoName from ".././images/caredesk360  new logo-04.png";

/* ------------------------------------------------------------------
   BRAND TOKENS — change these and the whole page re-themes
------------------------------------------------------------------ */
const BRAND = {
  primary: "#0F52BA",
  accent: "#5F9AA6",
  primaryDark: "#0B3E8C",
  ink: "#10233F",
  muted: "#5B6B84",
  line: "#E3E9F2",
  field: "#F7F9FC",
};

const GRADIENT = `linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.accent} 100%)`;

/* ------------------------------------------------------------------
   SHARED FIELD STYLE — one object instead of sx on every input
------------------------------------------------------------------ */
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "14px",
    bgcolor: BRAND.field,
    transition: "background-color .25s ease, box-shadow .25s ease",
    "& fieldset": { borderColor: BRAND.line },
    "&:hover": {
      bgcolor: "#fff",
      "& fieldset": { borderColor: BRAND.accent },
    },
    "&.Mui-focused": {
      bgcolor: "#fff",
      boxShadow: "0 0 0 4px rgba(15,82,186,.10)",
      "& fieldset": { borderColor: BRAND.primary, borderWidth: "1.5px" },
    },
    "&.Mui-error.Mui-focused": { boxShadow: "0 0 0 4px rgba(211,47,47,.10)" },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: BRAND.primary },
  "& .MuiFormHelperText-root": { ml: 0.5, fontSize: 12.5 },
};

/* Address on step 2: stretches to match the three stacked fields beside it */
const addressSx = {
  ...fieldSx,
  height: { md: "95%" },
  "& .MuiOutlinedInput-root": {
    ...fieldSx["& .MuiOutlinedInput-root"],
    height: { md: "95%" },
    alignItems: "flex-start",
    py: 1.75,
  },
  "& .MuiInputBase-inputMultiline": {
    height: { md: "95% !important" },
    overflow: "auto !important",
  },
};

/* Full-width cell inside the responsive form grid */
const fullWidth = { gridColumn: { sm: "1 / -1" } };

/* Step transition — reused by every step container */
const stepAnim = {
  "@keyframes stepIn": {
    from: { opacity: 0, transform: "translateY(10px)" },
    to: { opacity: 1, transform: "none" },
  },
  animation: "stepIn .35s ease",
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

/* Standard two-column form grid */
const gridSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
  columnGap: { sm: 2.5, md: 3 },
  rowGap: { xs: 2.25, sm: 2.75 },
  ...stepAnim,
};

const genderOptions = ["male", "female", "other"];
const bloodGroups = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/* status is fixed to Active (1) — no field is shown for it */
const INITIAL_FORM = {
  name: "",
  email: "",
  password: "",
  mobile: "",
  address: "",
  gender: "",
  dob: "",
  blood_group: "",
  status: 1,
};

/* ------------------------------------------------------------------
   STEPS — each step owns the fields it must validate before moving on
------------------------------------------------------------------ */
const STEPS = [
  {
    label: "Login details",
    caption: "How the patient signs in",
    heading: "Create the login",
    sub: "These credentials let the patient open their portal and see appointments.",
    fields: ["name", "email", "password", "mobile"],
  },
  {
    label: "Personal details",
    caption: "Identity and address",
    heading: "Add personal details",
    sub: "Helps staff identify the patient correctly during a visit. All optional.",
    fields: ["dob"],
  },
  {
    label: "Review",
    caption: "Check before saving",
    heading: "Review and create",
    sub: "Confirm everything reads correctly, then create the patient account.",
    fields: [],
  },
];

const LAST_STEP = STEPS.length - 1;

/* ------------------------------------------------------------------
   HELPERS
------------------------------------------------------------------ */
const todayISO = new Date().toISOString().split("T")[0];

const calcAge = (dob) => {
  if (!dob) return null;
  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const m = now.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age -= 1;
  return age >= 0 ? age : null;
};

const passwordScore = (pw) => {
  if (!pw) return 0;
  let score = 0;
  if (pw.length >= 6) score += 1;
  if (pw.length >= 10) score += 1;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score += 1;
  if (/\d/.test(pw) && /[^A-Za-z0-9]/.test(pw)) score += 1;
  return score; // 0 – 4
};

const STRENGTH = [
  { label: "Too short", color: "#C62828" },
  { label: "Weak", color: "#E65100" },
  { label: "Fair", color: "#B58900" },
  { label: "Good", color: "#2E7D32" },
  { label: "Strong", color: "#1B5E20" },
];

const validateField = (name, value) => {
  switch (name) {
    case "name":
      if (!value.trim()) return "Enter the patient's full name.";
      if (value.trim().length < 3) return "Name must be at least 3 characters.";
      return "";
    case "email":
      if (!value.trim()) return "Enter an email address.";
      if (!EMAIL_RE.test(value.trim()))
        return "Enter a valid email, e.g. name@clinic.com";
      return "";
    case "password":
      if (!value) return "Create a password for this account.";
      if (value.length < 6) return "Use at least 6 characters.";
      return "";
    case "mobile":
      if (!value) return "Mobile number must be 10 digits.";
      if (value && value.length !== 10)
        return "Mobile number must be 10 digits.";
      return "";
    case "dob":
      if (value && value > todayISO)
        return "Date of birth cannot be in the future.";
      return "";
    default:
      return "";
  }
};

/* ------------------------------------------------------------------
   STEP RAIL — vertical, lives in the brand panel on desktop
------------------------------------------------------------------ */
const StepRail = ({ activeStep, onJump }) => (
  <Stack spacing={0} sx={{ position: "relative", zIndex: 1 }}>
    {STEPS.map((step, i) => {
      const done = i < activeStep;
      const active = i === activeStep;

      return (
        <Box key={step.label}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            onClick={() => done && onJump(i)}
            sx={{
              cursor: done ? "pointer" : "default",
              opacity: active || done ? 1 : 0.5,
              transition: "opacity .3s ease",
            }}
          >
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
                fontSize: 14,
                fontWeight: 800,
                color: active ? BRAND.primary : "#fff",
                bgcolor: active ? "#fff" : "transparent",
                border: `1.5px solid ${done ? "#fff" : "rgba(255,255,255,.55)"}`,
                boxShadow: active ? "0 0 0 5px rgba(255,255,255,.18)" : "none",
                transition: "all .3s ease",
              }}
            >
              {done ? <Check sx={{ fontSize: 18 }} /> : i + 1}
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: active ? 700 : 600,
                  fontSize: 15.5,
                  lineHeight: 1.3,
                }}
              >
                {step.label}
              </Typography>
              <Typography sx={{ fontSize: 12.5, opacity: 0.75 }}>
                {step.caption}
              </Typography>
            </Box>
          </Stack>

          {i < STEPS.length - 1 && (
            <Box
              sx={{
                width: 2,
                height: 26,
                ml: "16px",
                my: 0.75,
                borderRadius: 2,
                bgcolor: i < activeStep ? "#fff" : "rgba(255,255,255,.28)",
                transition: "background-color .3s ease",
              }}
            />
          )}
        </Box>
      );
    })}
  </Stack>
);

/* ------------------------------------------------------------------
   REVIEW CELL — one cell of the review grid.
   `span` makes it take the full row; otherwise two sit side by side.
------------------------------------------------------------------ */
const ReviewCell = ({ label, value, span = false, oneLine = false }) => (
  <Box
    sx={{
      bgcolor: "#fff",
      px: { xs: 2, sm: 2.5 },
      py: 1.75,
      minWidth: 0,
      display: "flex",
      flexWrap: oneLine ? "nowrap" : "wrap",
      gap: 1,
      alignItems: "center",
      gridColumn: span ? { sm: "1 / -1" } : "auto",
    }}
  >
    <Typography
      sx={{
        fontSize: { xs: 12, md: 14 },
        fontWeight: 700,
        letterSpacing: ".5px",
        textTransform: "capitalize",
        color: BRAND.muted,
        flexShrink: 0,
      }}
    >
      {label} :
    </Typography>

    <Typography
      title={oneLine && value ? value : undefined}
      sx={{
        fontSize: { xs: 12, md: 14 },
        fontWeight: value ? 600 : 400,
        color: value ? BRAND.ink : "#9AA7BC",
        minWidth: 0,
        ...(oneLine
          ? {
              flex: 1,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }
          : { wordBreak: "break-word", whiteSpace: "pre-line" }),
      }}
    >
      {value || "Not provided"}
    </Typography>
  </Box>
);
/* ==================================================================
   PATIENT REGISTER — 3 step wizard
================================================================== */
const PatientRegister = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});

  const age = useMemo(() => calcAge(formData.dob), [formData.dob]);
  const score = useMemo(
    () => passwordScore(formData.password),
    [formData.password],
  );
  const step = STEPS[activeStep];

  const handleChange = (e) => {
    let { name, value } = e.target;

    if (name === "mobile") value = value.replace(/\D/g, "").slice(0, 10);
    if (name === "name") value = value.replace(/[^\p{L}\s.'-]/gu, "");

    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: "" } : prev));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM);
    setErrors({});
    setShowPassword(false);
    setActiveStep(0);
  };

  /* Validates only the fields belonging to the current step */
  const checkCurrentStep = () => {
    const found = {};
    step.fields.forEach((key) => {
      const msg = validateField(key, formData[key]);
      if (msg) found[key] = msg;
    });

    setErrors((prev) => ({ ...prev, ...found }));

    const firstKey = Object.keys(found)[0];
    if (firstKey) {
      toast.error(found[firstKey]);
      const el = document.querySelector(`[name="${firstKey}"]`);
      if (el) el.focus();
      return false;
    }
    return true;
  };

  const goNext = () => {
    if (!checkCurrentStep()) return;
    setActiveStep((s) => Math.min(s + 1, LAST_STEP));
  };

  const goBack = () => setActiveStep((s) => Math.max(s - 1, 0));

  const createPatient = async () => {
    if (loading) return;

    /* final safety net — re-check every required field */
    const found = {};
    STEPS.forEach((s) =>
      s.fields.forEach((key) => {
        const msg = validateField(key, formData[key]);
        if (msg) found[key] = msg;
      }),
    );

    if (Object.keys(found).length) {
      setErrors(found);
      const badStep = STEPS.findIndex((s) => s.fields.some((f) => found[f]));
      setActiveStep(badStep);
      toast.error(Object.values(found)[0]);
      return;
    }

    const payload = {
      ...formData,
      name: formData.name.trim(),
      email: formData.email.trim().toLowerCase(),
      address: formData.address.trim(),
      status: 1, // always created as Active
    };

    try {
      setLoading(true);

      const res = await axios.post(
        `${process.env.REACT_APP_URL}/api/patient/create`,
        payload,
        { withCredentials: true },
      );

      toast.success(res.data.message || "Patient account created.");
      resetForm();
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Could not create the patient. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  /* Enter key advances the wizard instead of submitting early */
  const handleSubmit = (e) => {
    e.preventDefault();
    if (activeStep === LAST_STEP) createPatient();
    else goNext();
  };

  const navigate = useNavigate();

  /* true once the user has typed anything */
  const isDirty = useMemo(
    () =>
      Object.keys(INITIAL_FORM).some(
        (k) => k !== "status" && formData[k] !== INITIAL_FORM[k],
      ),
    [formData],
  );

  const handleExit = () => {
    if (
      !window.confirm(
        "Leave this page? The details you've entered will be lost.",
      )
    )
      return;
    navigate("/");
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: { xs: 0, sm: 3, md: 4, lg: 6 },
        bgcolor: "#EEF3F9",
        backgroundImage: `
          radial-gradient(1100px 520px at 8% -10%, rgba(15,82,186,.16), transparent 60%),
          radial-gradient(900px 480px at 100% 110%, rgba(95,154,166,.22), transparent 60%)
        `,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 1080,
          borderRadius: { xs: 0, sm: "24px" },
          overflow: "hidden",
          border: { xs: "none", sm: `1px solid ${BRAND.line}` },
          boxShadow: { xs: "none", sm: "0 30px 70px -30px rgba(16,35,63,.35)" },
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(0,380px) minmax(0,1fr)",
          },
        }}
      >
        {/* ============ LEFT PANEL — brand + step rail (desktop) ============ */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            justifyContent: "space-between",
            p: { md: 5, lg: 6 },
            color: "#fff",
            position: "relative",
            background: `linear-gradient(165deg, ${BRAND.primary} 0%, ${BRAND.primaryDark} 45%, ${BRAND.accent} 100%)`,
            "&::after": {
              content: '""',
              position: "absolute",
              inset: 0,
              background:
                "radial-gradient(420px 320px at 85% 8%, rgba(255,255,255,.18), transparent 65%)",
              pointerEvents: "none",
            },
          }}
        >
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: "100%",
                  height: "60%",
                  borderRadius: "16px",
                  bgcolor: "#fff",
                  display: "grid",
                  placeItems: "center",
                  p: 0.75,
                  boxShadow: "0 18px 40px -18px rgba(0,0,0,.6)",
                }}
              >
                <Box
                  component="img"
                  src={logoName}
                  alt="CareDesk360"
                  sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              </Box>
            </Stack>

            <Typography sx={{ mt: 2, fontWeight: 700, fontSize: 14 }}>
              New patient registration
            </Typography>
            <Typography
              sx={{ mt: 0.75, fontSize: 14, opacity: 0.8, maxWidth: 290 }}
            >
              Three short steps. Nothing is saved until you create the account
              on the last one.
            </Typography>
          </Box>

          <Box sx={{ my: 2 }}>
            <StepRail activeStep={activeStep} onJump={setActiveStep} />
          </Box>

          <Typography
            sx={{
              position: "relative",
              zIndex: 1,
              fontSize: 12.5,
              opacity: 0.6,
            }}
          >
            © {new Date().getFullYear()} CareDesk360
          </Typography>
        </Box>

        {/* ============ MOBILE HEADER ============ */}
        <Box
          sx={{
            display: { xs: "block", md: "none" },
            px: { xs: 2.5, sm: 4 },
            py: 2.25,
            background: GRADIENT,
            color: "#fff",
          }}
        >
          <Stack direction="row" spacing={1.75} alignItems="center">
            <Box
              sx={{
                width: "100%",
                height: "100%",
                borderRadius: "12px",
                bgcolor: "#fff",
                display: "grid",
                placeItems: "center",
                p: 0.5,
                flexShrink: 0,
              }}
            >
              <Box
                component="img"
                src={logoName}
                alt="CareDesk360"
                sx={{ width: "100%", height: "100%", objectFit: "contain" }}
              />

              <Typography sx={{ fontSize: 12.5, opacity: 0.85, color: "#000" }}>
                Step {activeStep + 1} of {STEPS.length} · {step.label}
              </Typography>
            </Box>
          </Stack>

          {/* segmented progress */}
          <Stack direction="row" spacing={0.75} sx={{ mt: 1.75 }}>
            {STEPS.map((s, i) => (
              <Box
                key={s.label}
                sx={{
                  flex: 1,
                  height: 4,
                  borderRadius: 2,
                  bgcolor: i <= activeStep ? "#fff" : "rgba(255,255,255,.3)",
                  transition: "background-color .3s ease",
                }}
              />
            ))}
          </Stack>
        </Box>

        {/* ============ RIGHT PANEL — the form ============ */}
        <Box
          sx={{
            bgcolor: "#fff",
            px: { xs: 2.5, sm: 4, md: 4, lg: 6 },
            py: { xs: 3, sm: 4, md: 4 },
            display: "flex",
            flexDirection: "column",
            minHeight: { md: 400 },
            overflowY: { md: "auto" },
            "&::-webkit-scrollbar": { width: 8 },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "#D6DEEA",
              borderRadius: 8,
            },
          }}
        >
          <Box
            sx={{
              color: "#ff0000",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 700,
                color: BRAND.accent,
                letterSpacing: ".6px",
              }}
            >
              STEP {activeStep + 1} OF {STEPS.length}
            </Typography>

            <Tooltip title="Back to website" arrow placement="left">
              <Button
                onClick={handleExit}
                aria-label="Back to website"
                sx={{ color: "#ff0000", fontSize: "10px" }}
              >
                <Close />
              </Button>
            </Tooltip>
          </Box>

          <Typography
            variant="h4"
            sx={{
              mt: 0.5,
              fontWeight: 800,
              color: BRAND.ink,
              letterSpacing: "-.5px",
              fontSize: { xs: 24, sm: 29, md: 32 },
            }}
          >
            {step.heading}
          </Typography>

          <Typography
            sx={{
              mt: 1,
              mb: { xs: 2, sm: 3 },
              color: BRAND.muted,
              fontSize: { xs: 14, sm: 15.5 },
            }}
          >
            {step.sub}
          </Typography>

          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{ flex: 1 }}
          >
            {/* ---------------- STEP 1 : LOGIN ---------------- */}
            {activeStep === 0 && (
              <Box key="step-0" sx={gridSx}>
                <TextField
                  fullWidth
                  required
                  label="Full name"
                  name="name"
                  autoComplete="name"
                  placeholder="e.g. Ananya Sharma"
                  value={formData.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(errors.name)}
                  helperText={errors.name || " "}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person sx={{ color: BRAND.primary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={fieldSx}
                />

                <TextField
                  fullWidth
                  required
                  type="email"
                  label="Email address"
                  name="email"
                  autoComplete="email"
                  placeholder="name@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(errors.email)}
                  helperText={
                    errors.email || "Used for login and appointment updates."
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ color: BRAND.primary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={fieldSx}
                />

                <Box>
                  <TextField
                    fullWidth
                    required
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    autoComplete="new-password"
                    value={formData.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(errors.password)}
                    helperText={errors.password || "At least 6 characters."}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock sx={{ color: BRAND.primary }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            edge="end"
                            aria-label={
                              showPassword ? "Hide password" : "Show password"
                            }
                            onClick={() => setShowPassword((s) => !s)}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={fieldSx}
                  />

                  {formData.password && (
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      sx={{ mt: -0.5, px: 0.5 }}
                    >
                      <LinearProgress
                        variant="determinate"
                        value={(score / 4) * 100}
                        sx={{
                          flex: 1,
                          height: 6,
                          borderRadius: 3,
                          bgcolor: BRAND.line,
                          "& .MuiLinearProgress-bar": {
                            borderRadius: 3,
                            bgcolor: STRENGTH[score].color,
                          },
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: 12.5,
                          fontWeight: 700,
                          color: STRENGTH[score].color,
                          minWidth: 62,
                        }}
                      >
                        {STRENGTH[score].label}
                      </Typography>
                    </Stack>
                  )}
                </Box>

                <TextField
                  fullWidth
                  required
                  label="Mobile number (WhatsApp)"
                  name="mobile"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  placeholder="10-digit number"
                  value={formData.mobile}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(errors.mobile)}
                  helperText={
                    errors.mobile ||
                    "Reminders & prescriptions will be sent to this WhatsApp number."
                  }
                  inputProps={{ maxLength: 10 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <WhatsApp sx={{ color: "#0f52ba" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={fieldSx}
                />
              </Box>
            )}

            {/* ---------------- STEP 2 : PERSONAL + ADDRESS ----------------
                Left column  : DOB, Gender, Blood group (stacked)
                Right column : Address, stretched to the same height
            ------------------------------------------------------------- */}
            {activeStep === 1 && (
              <Box
                key="step-1"
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(2, minmax(0, 1fr))",
                  },
                  columnGap: { md: 3 },
                  rowGap: { xs: 2.25, md: 0 },
                  alignItems: "stretch",
                  ...stepAnim,
                }}
              >
                {/* --- LEFT: three stacked fields --- */}
                <Box
                  sx={{
                    display: "grid",
                    rowGap: { xs: 2.25, sm: 0 },
                    alignContent: "start",
                  }}
                >
                  <TextField
                    fullWidth
                    type="date"
                    label="Date of birth"
                    name="dob"
                    value={formData.dob}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(errors.dob)}
                    helperText={
                      errors.dob || (age !== null ? `${age} years old` : " ")
                    }
                    slotProps={{
                      inputLabel: { shrink: true }, // ← use this instead of InputLabelProps
                      htmlInput: { max: todayISO }, // ← replaces inputProps
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarMonth sx={{ color: BRAND.primary }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={fieldSx}
                  />

                  <TextField
                    select
                    fullWidth
                    label="Gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    helperText=" "
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Wc sx={{ color: BRAND.primary }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={fieldSx}
                  >
                    {genderOptions.map((gender) => (
                      <MenuItem key={gender} value={gender}>
                        {gender.charAt(0).toUpperCase() + gender.slice(1)}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select
                    fullWidth
                    label="Blood group"
                    name="blood_group"
                    value={formData.blood_group}
                    onChange={handleChange}
                    helperText=" "
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Bloodtype sx={{ color: BRAND.primary }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={fieldSx}
                  >
                    {bloodGroups.map((group) => (
                      <MenuItem key={group} value={group}>
                        {group}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>

                {/* --- RIGHT: address, full height of the left column --- */}
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Address"
                  name="address"
                  autoComplete="street-address"
                  placeholder={
                    "House / flat, street\nArea, landmark\nCity, state, PIN code"
                  }
                  value={formData.address}
                  onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment
                        position="start"
                        sx={{ alignSelf: "flex-start", mt: 0.5 }}
                      >
                        <Home sx={{ color: BRAND.primary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={addressSx}
                />
              </Box>
            )}

            {/* ---------------- STEP 3 : REVIEW ---------------- */}
            {activeStep === 2 && (
              <Box key="step-2" sx={stepAnim}>
                <Box
                  sx={{
                    border: `1px solid ${BRAND.line}`,
                    borderRadius: "16px",
                    overflow: "hidden",
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, minmax(0, 1fr))",
                    },
                    gap: "1px",
                    bgcolor: BRAND.line, // the 1px gaps read as divider lines
                  }}
                >
                  <ReviewCell label="Full name" value={formData.name.trim()} />

                  <ReviewCell
                    label="Email"
                    value={formData.email.trim().toLowerCase()}
                  />

                  {/* side by side */}
                  <ReviewCell
                    label="Date of birth"
                    value={
                      formData.dob
                        ? `${formData.dob}${age !== null ? `  ·  ${age} years` : ""}`
                        : ""
                    }
                  />
                  <ReviewCell
                    label="Mobile number"
                    value={formData.mobile ? `+91 ${formData.mobile}` : ""}
                  />

                  {/* side by side */}
                  <ReviewCell
                    label="Blood group"
                    value={formData.blood_group}
                  />
                  <ReviewCell
                    label="Gender"
                    value={
                      formData.gender
                        ? formData.gender.charAt(0).toUpperCase() +
                          formData.gender.slice(1)
                        : ""
                    }
                  />

                  <ReviewCell
                    span
                    oneLine
                    label="Address"
                    value={formData.address.trim().replace(/\s*\n\s*/g, ", ")}
                  />
                </Box>

                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mt: 1.5, color: BRAND.muted }}
                >
                  <Check sx={{ fontSize: 17, color: "#2E7D32" }} />
                  <Typography sx={{ fontSize: 12.5 }}>
                    The account will be created as active. Use Back, or a
                    completed step in the sidebar, to change anything.
                  </Typography>
                </Stack>
              </Box>
            )}

            {/* ---------------- NAVIGATION ---------------- */}
            <Divider sx={{ my: { xs: 2.5, sm: 3 }, borderColor: BRAND.line }} />

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: { md: "center" },
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Button
                onClick={resetForm}
                disabled={loading}
                sx={{
                  alignSelf: { xs: "center", md: "auto" },
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 13.5,
                  color: BRAND.muted,
                  "&:hover": { color: "#C62828", bgcolor: "transparent" },
                }}
              >
                Reset
              </Button>

              <Stack
                direction={{ xs: "column-reverse", sm: "row" }}
                spacing={1.5}
                sx={{ width: { xs: "100%", md: "auto" }, flexShrink: 0 }}
              >
                <Button
                  variant="outlined"
                  onClick={goBack}
                  disabled={activeStep === 0 || loading}
                  startIcon={<ArrowBack />}
                  sx={{
                    height: 52,
                    px: 3.5,
                    borderRadius: "14px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 15,
                    color: BRAND.accent,
                    borderColor: BRAND.line,
                    "&:hover": {
                      borderColor: BRAND.primary,
                      color: BRAND.primary,
                      bgcolor: "#EEF5FF",
                    },
                  }}
                >
                  Back
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  disableElevation
                  endIcon={
                    activeStep === LAST_STEP || loading ? null : (
                      <ArrowForward />
                    )
                  }
                  sx={{
                    height: 52,
                    px: 5,
                    borderRadius: "14px",
                    textTransform: "none",
                    fontSize: 16,
                    fontWeight: 700,
                    color: "#fff",
                    background: GRADIENT,
                    boxShadow: "0 14px 30px -12px rgba(15,82,186,.65)",
                    transition: "transform .25s ease, box-shadow .25s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 18px 36px -12px rgba(15,82,186,.75)",
                      background: `linear-gradient(135deg, ${BRAND.primaryDark}, #4E8D99)`,
                    },
                    "&.Mui-disabled": { background: "#B9C6DA", color: "#fff" },
                    "@media (prefers-reduced-motion: reduce)": {
                      transition: "none",
                      "&:hover": { transform: "none" },
                    },
                  }}
                >
                  {loading ? (
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <CircularProgress size={20} color="inherit" />
                      <span>Saving…</span>
                    </Stack>
                  ) : activeStep === LAST_STEP ? (
                    "Create patient"
                  ) : (
                    "Continue"
                  )}
                </Button>
              </Stack>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default PatientRegister;
